import { bool, string, number, func } from "prop-types";
import React, { useState, useRef, useEffect } from "react";
import { twMerge } from "tailwind-merge";

import { useTranslation, Trans } from "@lib/i18n";

import Typography from "@components/shared/Typography";
import EcoBonusModal from "./EcoBonusModal";
import { FINANCING_COLLAPSABLE_CONTAINER } from "@components/Financing/Financing";

import { getFormattedPrice } from "@shared/v2/lib/getFormattedPrice";
import { financeGatewayTeaserType } from "@types";
import { useScrollToView } from "@hooks/useScrollToView";
import { useFormatText } from "@hooks/useFormatText";
import { useFinanceGatewayDataParser } from "hooks/useFinanceGatewayDataParser";
import { withTabNavigation } from "@hoc/withTabNavigation";

const AdditionalTextClickable = withTabNavigation("span");

const VACPriceDetail = ({
  isFinanceRequestError,
  withEcoBonus,
  amount,
  priceFormat,
  currencySymbol,
  withFinanceWidgetButton,
  onIsFinancingOpenChange,
  openInModal,
  onOpenFinanceDetail,
  teaser,
}) => {
  const { t } = useTranslation();
  const [isEcoBonusModalOpen, setIsEcoBonusModalOpen] = useState(false);
  const financeGatewayDataParser = useFinanceGatewayDataParser({});

  const formattedAmount = () =>
    getFormattedPrice(amount, priceFormat, currencySymbol);

  const scrollToView = useScrollToView();

  const financingCollapsableContainerRef = useRef(null);

  useEffect(() => {
    financingCollapsableContainerRef.current = document.getElementById(
      FINANCING_COLLAPSABLE_CONTAINER
    );
  }, []);

  return (
    <>
      {isFinanceRequestError ? (
        <Typography className="text-white text-lg text-center font-semibold m-auto max-w-80 px-4 text-balance">
          {t("financeDataErrorMessage")}
        </Typography>
      ) : (
        <div
          className={twMerge(
            "flex flex-col justify-center items-end h-full p-2 z-10 text-white",
            withFinanceWidgetButton &&
              "relative mr-2 sm:mr-6 after:content-[''] after:absolute after:top-1/5 after:bottom-1/5 after:-right-1 after:w-px after:bg-greyLight sm:after:-right-3",
            "sm:p-4 md:p-2 lg:px-0 lg:py-1"
          )}
          data-id="price-switcher-price-container"
        >
          <div className="flex flex-row flex-wrap items-baseline justify-end text-sm text-white text-right font-semibold">
            <span className="text-sm/1 align-bottom">
              {t("price.vac.startingFrom")}
            </span>
            <div className="flex">
              <Typography
                className={twMerge(
                  "text-white font-2xl/1 font-semibold pl-1",
                  amount < 100 ? "lg:text-2xl/1" : "lg:text-lg/1"
                )}
                data-id="price-switcher-formatted-price"
              >
                {formattedAmount()}
              </Typography>
              <sup className="top-2 text-xs">{t("price.b2c.month")}</sup>
            </div>
          </div>
          <div className="flex flex-col ml-auto text-right pt-1 text-xxs tracking-wider font-extrabold">
            {financeGatewayDataParser("primarytext", teaser).map(
              ({ parsedText }, index) => (
                <Typography
                  className="text-white text-right text-xxs font-semibold [&>span]:block [&>span]:text-base"
                  key={index}
                  dangerouslySetInnerHTML={{
                    __html: useFormatText(parsedText).join(" "),
                  }}
                />
              )
            )}
          </div>
          <Typography
            className="bold text-white text-xxs text-right tracking-wider font-extrabold"
            data-id="price-switcher-deliveryinfo-text"
          >
            <Trans>{t("price.vac.monthlyFee")}</Trans>
            {t("price.vac.openDetail") && t("price.vac.openDetail").length > 0 && (
              <AdditionalTextClickable
                className="block cursor-pointer underline"
                onClick={() => {
                  if (openInModal) {
                    onOpenFinanceDetail?.();

                    return;
                  }

                  onIsFinancingOpenChange?.(true);

                  scrollToView(financingCollapsableContainerRef);
                }}
              >
                {t("price.vac.openDetail")}
              </AdditionalTextClickable>
            )}
          </Typography>
        </div>
      )}
      {withEcoBonus && (
        <EcoBonusModal
          isOpen={isEcoBonusModalOpen}
          onClose={() => setIsEcoBonusModalOpen(false)}
        />
      )}
    </>
  );
};

VACPriceDetail.propTypes = {
  priceFormat: string.isRequired,
  currencySymbol: string.isRequired,
  isFinanceRequestError: bool,
  withEcoBonus: bool,
  amount: number,
  deposit: number,
  withDeliveryInfo: bool,
  withFinanceWidgetButton: bool,
  onIsFinancingOpenChange: func,
  openInModal: bool,
  onOpenFinanceDetail: func,
  teaser: financeGatewayTeaserType,
};

export default VACPriceDetail;
